import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useSession } from '../../auth/SessionContext';
import { ApiError, apiSend } from '../../lib/api';
import { Alert, Button, Card, LinkButton, PageHeader, TextField } from '../../ui';

/**
 * Sign in.
 *
 * The API's own sentence is what is shown when it refuses — "That email
 * address or password is not right" — because it is already written for a
 * person and rewriting it here would mean two places to keep true.
 *
 * An unconfirmed address CAN sign in now (the banner inside the app is where
 * it is asked for), so the one thing this page adds is the resend path for
 * somebody who never got the email: a form that always answers the same way,
 * because "that address has no account" is not something a sign-in page
 * should say to a stranger.
 */
export function Login(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendDone, setResendDone] = useState(false);
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiSend('POST', '/auth/login', { email, password });
      await refresh();
      navigate(params.get('next') ?? '/campaigns');
    } catch (err) {
      setError(
        err instanceof ApiError ? err : new ApiError(0, 'network', 'Could not reach the API.'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function resend(address: string): Promise<void> {
    // Always 204, whatever the address is, so this can never be read as an
    // answer to "does that account exist".
    await apiSend('POST', '/auth/resend-verification', { email: address }).catch(() => undefined);
  }

  async function resendFromForm(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    await resend(resendEmail.trim());
    setResendDone(true);
  }

  return (
    <>
      <PageHeader
        hero
        eyebrow="Nexus"
        title="Sign in"
        lead="Your campaigns, your submissions and your wallet."
      />

      <Card variant="feature">
        <form onSubmit={submit} className="form">
          <TextField
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            label="Password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        {error && (
          <Alert tone="critical">
            <span>
              {error.message}
              {/* Kept for an API that still refuses an unconfirmed address:
                  the password was right, and the fix is a new link. */}
              {error.code === 'email_not_verified' && !resent && (
                <>
                  {' '}
                  <LinkButton
                    onClick={() => {
                      void resend(email).then(() => setResent(true));
                    }}
                  >
                    Send the link again
                  </LinkButton>
                </>
              )}
              {error.code === 'email_not_verified' && resent && ' A new link is on its way.'}
            </span>
          </Alert>
        )}

        <p className="muted">
          {/* A standalone action, not a word inside a sentence: it gets a
              target a thumb can hit. */}
          <Link className="btn-link" to="/forgot-password">
            Forgotten your password?
          </Link>
        </p>
      </Card>

      <Card tight>
        <p className="muted">
          No account yet? <Link to="/signup">Create an account</Link> — it takes a minute.
        </p>
        <p className="muted">
          {!resendOpen ? (
            <LinkButton
              onClick={() => {
                setResendEmail(email);
                setResendOpen(true);
              }}
            >
              Didn't get your confirmation email?
            </LinkButton>
          ) : resendDone ? (
            <span>
              If that address has an account that is not confirmed yet, a new link is on its way.
              Check your spam folder too. You can sign in meanwhile; confirming is only needed to
              submit clips and request payouts.
            </span>
          ) : (
            <span>
              Sign in and press <strong>Resend</strong> on the banner, or send a fresh link to
              your address now.
            </span>
          )}
        </p>
        {resendOpen && !resendDone && (
          <form onSubmit={resendFromForm} className="form">
            <TextField
              label="Email for the confirmation link"
              type="email"
              name="resendEmail"
              autoComplete="email"
              required
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
            />
            <Button type="submit">Resend the confirmation email</Button>
          </form>
        )}
      </Card>
    </>
  );
}
